import { Tooltip } from "@src/components/elements";
import { PayrollIcon } from "@src/components/elements/assets/PayrollIcon";
import { ChipStyledRoot } from "@src/components/elements/Chip";
import React from "react";

type Props =
  | {
      type: "admin" | "super viewer" | "member";
      hasPayroll?: undefined;
    }
  | {
      type: "department lead" | "business unit lead";
      hasPayroll: boolean;
    };

const permissionTypeToColor: {
  [key in Props["type"]]: string;
} = {
  admin: "#EDE0FF",
  "super viewer": "#E8FEFF",
  "department lead": "#E8EDFF",
  "business unit lead": " #E8FEFF",
  member: "#F0F0F0",
};

function getLabel(
  type: Props["type"],
): "Admin" | "Super Viewer" | "Dept. Lead" | "B.U. Lead" | "Member" {
  switch (type) {
    case "admin":
      return "Admin";
    case "super viewer":
      return "Super Viewer";
    case "department lead":
      return "Dept. Lead";
    case "business unit lead":
      return "B.U. Lead";
    case "member":
      return "Member";
    default:
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled type: ${exhaustiveCheck}`);
  }
}

const PermissionChip: React.FC<Props> = ({ type, hasPayroll }) => {
  return hasPayroll ? (
    <Tooltip title="Has access to payroll data" showWithoutOverflow={true}>
      <ChipStyledRoot
        backgroundColor={permissionTypeToColor[type]}
        disabled={false}
      >
        <span className="label">{getLabel(type)}</span>
        {hasPayroll && <PayrollIcon />}
      </ChipStyledRoot>
    </Tooltip>
  ) : (
    <ChipStyledRoot
      backgroundColor={permissionTypeToColor[type]}
      disabled={false}
    >
      <span className="label">{getLabel(type)}</span>
    </ChipStyledRoot>
  );
};

export const PermissionChips: React.FC<{
  permissions: {
    hasAdminAccess: boolean;
    hasSuperViewerAccess: boolean;
    hasDepartmentAccess: boolean;
    hasDepartmentPayrollAccess: boolean;
    hasBusinessUnitAccess: boolean;
    hasBusinessUnitPayrollAccess: boolean;
  };
}> = ({
  permissions: {
    hasAdminAccess,
    hasSuperViewerAccess,
    hasDepartmentAccess,
    hasDepartmentPayrollAccess,
    hasBusinessUnitAccess,
    hasBusinessUnitPayrollAccess,
  },
}) => {
  const props: Parameters<typeof PermissionChip>[0][] = [];

  if (hasAdminAccess) {
    props.push({ type: "admin" });
  }
  if (hasSuperViewerAccess) {
    props.push({ type: "super viewer" });
  }
  if (hasDepartmentAccess) {
    props.push({
      type: "department lead",
      hasPayroll: hasDepartmentPayrollAccess,
    });
  }
  if (hasBusinessUnitAccess) {
    props.push({
      type: "business unit lead",
      hasPayroll: hasBusinessUnitPayrollAccess,
    });
  }
  if (props.length === 0) {
    props.push({ type: "member" });
  }

  return (
    <div className="row">
      {props.map((p, i) => (
        <PermissionChip key={i} {...p} />
      ))}
    </div>
  );
};
